import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Flip is defined for curve and mesh dims only (T ∈ {1, 2}); the class generic is left as
// `IndexingDim` and the variants pin it.
export type FlipOperatorParameters<TAttributeSet> = {
    name: string
    attributeSet: TAttributeSet | null
}

export const flipOperatorParametersSchema = <TAttributeSet>(attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>) =>
    z.object({
        name: z.string(),
        attributeSet: z.nullable(attributeSetSchema),
    })

function applyFlipOperator<T extends Exclude<IndexingDim, 0>>(attributeSet: GeometryAttributeSetData<T>): GeometryAttributeSetData<T> {
    const attrRefSetData = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)
    const flippedAttrRefDataSet = applyGeometryOperator((attrRefSet: AttributeRefSet) => Operators.flip(attrRefSet), attrRefSetData)
    return geometryAttributeRefDataSetToGeometryAttributeSetData(flippedAttrRefDataSet, attributeSet.dim)
}

export abstract class GeometryFlipOperatorNode<
    out T extends Exclude<IndexingDim, 0>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, FlipOperatorParameters<TAttributeSet>> {
    protected override hasValidParameters(
        parameters: FlipOperatorParameters<TAttributeSet>,
    ): parameters is FlipOperatorParameters<TAttributeSet> & NonNullableParameters<FlipOperatorParameters<TAttributeSet>> {
        return parameters.attributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<FlipOperatorParameters<TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attributeSet} = parameters

        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(evaluator.evaluate(attributeSet))

        const geometryAttributeSetData = scope.pureLambda(attributeSetDataValid, (attributeSetData) => applyFlipOperator(attributeSetData), "applyFlipOperator")

        return {evaluatedGeometryData: geometryAttributeSetData, invalidator: attributeSetDataInvalid}
    }
}
